import React, { useState, useEffect, useRef } from 'react'
import Styles from './GraphCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import IFText from 'Components/IFText/IFText'
import IFSelectPicker from 'Components/IFFilter/IFSelectPicker/IFSelectPicker'
import IFFilterType from 'Enums/IFFilterType'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import { useTranslation } from 'react-i18next'

const GraphCard = ({
  title,
  icon,
  isSelectPicker,
  graph: Graph,
  selectTitle,
  selectData,
  selectIcon,
  currency,
  filter: externalFilter,
}) => {
  const graphRef = useRef()
  const [value, setValue] = useState('')
  const [internalFilter, setInternalFilter] = useState([])
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    if (externalFilter && isSelectPicker) {
      const periodFilter = externalFilter.find((f) => f.field === 'Period')
      if (periodFilter) {
        const matchedItem = selectData.find(
          (item) =>
            item.label === periodFilter.value ||
            item.value === periodFilter.value,
        )
        if (matchedItem && matchedItem.value !== value) {
          setValue(matchedItem.value)
        }
      }
    }
  }, [externalFilter, isSelectPicker, selectData])

  useEffect(() => {
    if (!externalFilter) return

    if (isSelectPicker) {
      const periodFilter = {
        type: IFFilterType.SELECT,
        title: 'Period',
        data: selectData,
        initialValue: value,
        value: value,
        field: 'Period',
      }

      const combinedFilter = [...externalFilter]
      const periodIndex = combinedFilter.findIndex((f) => f.field === 'Period')

      if (periodIndex >= 0) {
        combinedFilter[periodIndex] = {
          ...combinedFilter[periodIndex],
          value: value,
        }
      } else {
        combinedFilter.push(periodFilter)
      }
      setInternalFilter(combinedFilter)
    } else {
      setInternalFilter(externalFilter)
    }
  }, [externalFilter, value, isSelectPicker, selectData])

  useEffect(() => {
    const timeout = setTimeout(() => setIsFirstLoad(false), 300)
    return () => clearTimeout(timeout)
  }, [])

  return (
    <div
      style={{
        backgroundColor: Colors.white,
        border: `1px solid ${Colors.CardShadow}`,
      }}
      className={Styles.Card}
    >
      <div
        className={Styles.TopContainer}
        style={{
          borderBottom: `1px dashed ${Colors.CardShadow}`,
        }}
      >
        <div className={Styles.Left}>
          <div
            className={Styles.IconContainer}
            style={{ backgroundColor: Colors.Analytics.IconBackground }}
          >
            <div className={Styles.Icon}>
              {isFirstLoad ? (
                <IFSkeleton variant="circular" width={24} height={24} />
              ) : (
                icon
              )}
            </div>
          </div>
          {isFirstLoad ? (
            <IFSkeleton
              variant="text"
              width={120}
              height={24}
              style={{ marginLeft: 8 }}
            />
          ) : (
            <IFText style={{ color: Colors.black }} className={Styles.Title}>
              {title}
            </IFText>
          )}
        </div>

        {isSelectPicker ? (
          isFirstLoad ? (
            <div className={Styles.Date}>
              <IFSkeleton variant="rectangular" width={120} height={36} />
            </div>
          ) : (
            <div
              className={Styles.Date}
              style={{ border: `1px solid ${Colors.CardShadow}` }}
            >
              <div className={Styles.SelectIcon}> {selectIcon} </div>
              {value && (
                <IFSelectPicker
                  width={120}
                  title={
                    selectTitle
                      ? selectTitle
                      : selectData.find((item) => item.value === value)
                          ?.label || value
                  }
                  data={selectData.map((item) => item.label)}
                  initialValue={
                    selectData.find((item) => item.value === value)?.label ||
                    value
                  }
                  externalValue={
                    selectData.find((item) => item.value === value)?.label
                  }
                  onValueChange={(selectedLabel) => {
                    const matchedItem = selectData.find(
                      (item) => item.label === selectedLabel,
                    )
                    const camelCase = matchedItem?.value || selectedLabel
                    setValue(camelCase)
                  }}
                />
              )}
            </div>
          )
        ) : (
          <div
            className={Styles.DateAllTime}
            style={{ border: `1px solid ${Colors.CardShadow}` }}
          >
            <div className={Styles.SelectIcon}> {selectIcon} </div>
            <IFText>{selectTitle}</IFText>
          </div>
        )}
      </div>
      <Graph
        ref={graphRef}
        className={Styles.Graph}
        value={value}
        filter={internalFilter}
        currency={currency}
      />
    </div>
  )
}

GraphCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.element,
  isSelectPicker: PropTypes.bool,
  graph: PropTypes.element,
  selectTitle: PropTypes.string,
  selectData: PropTypes.array,
  selectIcon: PropTypes.element,
  currency: PropTypes.string,
  filter: PropTypes.array,
}

export default GraphCard
